// src/pages/vas/VABrowsePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, MapPin, Star, DollarSign, Briefcase,
  CheckCircle, Clock, Award, TrendingUp, Users, ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { jobService } from '../../services/jobService';
import { useAuth } from '../../context/AuthContext';

const VABrowsePage = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [vas, setVas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    skills: [],
    minRating: 0,
    availability: 'all',
    priceRange: 'all'
  });

  const popularSkills = [
    'Administrative Support',
    'Data Entry',
    'Customer Service',
    'Content Writing',
    'Social Media',
    'Email Management',
    'Bookkeeping',
    'Research'
  ];

  useEffect(() => {
    fetchVAs();
  }, [filters]);

  const fetchVAs = async () => {
    try {
      setLoading(true);
      setError(null);
      const vasData = await jobService.searchVAs(filters);
      setVas(vasData);
    } catch (err) {
      console.error('Error fetching VAs:', err);
      setError(err.message || 'Failed to load virtual assistants');
    } finally {
      setLoading(false);
    }
  };

  const handleSkillToggle = (skill) => {
    setFilters(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatRate = (rate) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rate);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      skills: [],
      minRating: 0,
      availability: 'all',
      priceRange: 'all'
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.skills.length > 0) count++;
    if (filters.availability !== 'all') count++;
    if (filters.priceRange !== 'all') count++;
    if (filters.minRating > 0) count++;
    return count;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Browse Virtual Assistants</h1>
          <p className="text-gray-600">Find skilled VAs for your business needs</p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name, skills, or expertise..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <select
                value={filters.availability}
                onChange={(e) => handleFilterChange('availability', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Availability</option>
                <option value="available">Available Now</option>
                <option value="limited">Limited Availability</option>
                <option value="busy">Busy</option>
              </select>
            </div>

            <div>
              <select
                value={filters.priceRange}
                onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Price Ranges</option>
                <option value="0-50">R0 - R50/hr</option>
                <option value="50-100">R50 - R100/hr</option>
                <option value="100-200">R100 - R200/hr</option>
                <option value="200-500">R200 - R500/hr</option>
                <option value="500-+">R500+/hr</option>
              </select>
            </div>
          </div>

          {/* Skills Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Skills</label>
            <div className="flex flex-wrap gap-2">
              {popularSkills.map(skill => (
                <button
                  key={skill}
                  onClick={() => handleSkillToggle(skill)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    filters.skills.includes(skill)
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                  }`}
                >
                  {skill}
                </button>
              ))}
            </div>
          </div>

          {/* Active Filters */}
          {getActiveFilterCount() > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
              </span>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <h4 className="text-red-800 font-medium">Error loading VAs</h4>
              <p className="text-red-700 text-sm mt-1">{error}</p>
              <button
                onClick={fetchVAs}
                className="mt-2 text-red-700 hover:text-red-800 text-sm font-medium"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {/* Results Header */}
        <div className="mb-4 text-gray-600">
          Showing {vas.length} virtual assistant{vas.length !== 1 ? 's' : ''}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : vas.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border">
            <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No VAs found</h3>
            <p className="text-gray-600 mb-4">
              {getActiveFilterCount() > 0 
                ? 'Try adjusting your filters to see more results' 
                : 'No virtual assistants available at the moment'}
            </p>
            {getActiveFilterCount() > 0 && (
              <button
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {vas.map((va) => (
              <VACard key={va.id} va={va} formatRate={formatRate} navigate={navigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// VA Card Component
const VACard = ({ va, formatRate, navigate }) => {
  return (
    <div className="bg-white border rounded-lg p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
          {va.name?.charAt(0).toUpperCase() || 'V'}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{va.name || 'Virtual Assistant'}</h3>
          <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
            <div className="flex items-center">
              <Star className="text-yellow-500 mr-1" size={16} fill="currentColor" />
              <span className="font-medium">{va.rating?.toFixed(1) || '5.0'}</span>
              <span className="ml-1">({va.reviewCount || 0})</span>
            </div>
            <span className="text-gray-400">•</span>
            <div className="flex items-center">
              <Briefcase className="mr-1" size={14} />
              {va.completedJobs || 0} jobs
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-3">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              va.availability === 'available' 
                ? 'bg-green-100 text-green-800' 
                : va.availability === 'limited'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {va.availability === 'available' 
                ? 'Available Now' 
                : va.availability === 'limited'
                ? 'Limited Availability'
                : 'Busy'}
            </span>
            {va.hourlyRate > 0 && (
              <div className="flex items-center text-sm text-gray-600">
                <DollarSign size={14} className="mr-1" />
                <span className="font-semibold">{formatRate(va.hourlyRate)}/hr</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-gray-700 text-sm mb-4 line-clamp-2">{va.bio || 'No bio available'}</p>

      {/* Skills */}
      {va.skills && va.skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {va.skills.slice(0, 4).map((skill, index) => (
            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
              {skill}
            </span>
          ))}
          {va.skills.length > 4 && (
            <span className="text-blue-600 text-xs font-medium px-2 py-1">
              +{va.skills.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Location */}
      {va.location && (
        <div className="flex items-center text-sm text-gray-600 mb-4">
          <MapPin size={14} className="mr-1" />
          {va.location}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={() => navigate(`/vas/${va.id}`)}
          className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
        >
          View Profile
        </button>
        <button
          onClick={() => navigate('/jobs/post', { state: { preferredVA: va.id } })}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Hire
        </button>
      </div>
    </div>
  );
};

export default VABrowsePage;